/* eslint-disable @typescript-eslint/naming-convention */
import * as t from "io-ts";

import { stringEnumCodec } from "../codecs";
import {
  initialReferringPageCodec,
  landingPageCodec,
  sharedDonationCodec,
} from "../codecs/cookies";

export enum UTM_QUERY_PARAM {
  utm_source = "utm_source",
  utm_medium = "utm_medium",
  utm_campaign = "utm_campaign",
  utm_term = "utm_term",
  utm_content = "utm_content",
}

export const UTM_QUERY_PARAM_TO_VAR_NAME = {
  [UTM_QUERY_PARAM.utm_source]: "utmSource",
  [UTM_QUERY_PARAM.utm_medium]: "utmMedium",
  [UTM_QUERY_PARAM.utm_campaign]: "utmCampaign",
  [UTM_QUERY_PARAM.utm_term]: "utmTerm",
  [UTM_QUERY_PARAM.utm_content]: "utmContent",
};

export const utmMetadataCodec = t.partial({
  utmCampaign: t.string,
  utmMedium: t.string,
  utmSource: t.string,
  utmTerm: t.string,
  utmContent: t.string,
});

export enum UserSignUpSourceUserFacing {
  email = "Email",
  webSearch = "Web search",
  podcast = "Podcast",
  blogPostOrArticle = "Blog post or article",
  paypalGrants = "Paypal grants",
  wordOfMouth = "Word of mouth",
  conferenceOrEvent = "Conference or event",
  socialMedia = "Social media",
  ai = "ChatGPT or other AI",
  mailer = "Postcard or other mail",
  other = "Other",
}

export const userSignupSourceCodec = t.partial({
  type: stringEnumCodec({
    name: "Source",
    enumObject: UserSignUpSourceUserFacing,
  }),
  other: t.string,
});

export type UserSignupSource = t.TypeOf<typeof userSignupSourceCodec>;

export const signupMetadataCodec = t.partial({
  sharedDonation: sharedDonationCodec,
  initialReferrer: initialReferringPageCodec,
  landingPage: landingPageCodec,
  source: userSignupSourceCodec,
});

export type UtmMetadata = t.TypeOf<typeof utmMetadataCodec>;

export enum DeviceType {
  iphone = "iphone",
  android = "android",
  desktop = "desktop",
  tablet = "tablet",
  unknown = "unknown",
}

export const analyticsCodec = t.partial({
  userAgent: t.string,
  device: stringEnumCodec({ name: "Device", enumObject: DeviceType }),
  paymentTab: t.string,
});

/**
 * Most of our event names are auto-generated based on tname, but this tracks
 * the names of events that we are tracking more explicitly.
 */
export enum CustomEvent {
  // Triggered when we show the donation modal
  DONATE_SHOW_MODAL = "DONATE_SHOW_MODAL",
  // Triggered when the user completes a donation -- see trackDonate
  DONATE = "DONATE",
  // Triggered when the user starts building their profile
  START_BUILD_PROFILE = "START_BUILD_PROFILE",
  // Triggered when the user completes building profile signs up -- see trackSignup
  USER_SIGN_UP = "USER_SIGN_UP",
  // Triggered when a user attempts to login -- see trackLoginAttempt
  USER_LOGIN_ATTEMPT = "USER_LOGIN_ATTEMPT",
  // Triggered when the user clicks a specific share button
  SHARE_BUTTON_CLICK = "SHARE_BUTTON_CLICK",
  // Triggered when the user clicks a button in a feed
  FEED_BUTTON_CLICK = "FEED_BUTTON_CLICK",
  // Triggered when the user submits a search using the search bar
  SUBMIT_SEARCH = "SUBMIT_SEARCH",
  // Triggered when the user clicks a suggestion via search
  SEARCH_SUGGESTION_CLICKED = "SEARCH_SUGGESTION_CLICKED",
  // Triggered when a user opens the notification drawer and views notifications
  NOTIFICATIONS_VIEWED = "NOTIFICATIONS_VIEWED",
  // Experiment triggered, new experimental event comparing to $experiment_started
  EXPERIMENT_TRIGGERED = "EXPERIMENT_TRIGGERED",
  SEARCH_EXACT_CAUSE = "SEARCH_EXACT_CAUSE",
  FREQUENCY_ERROR = "FREQUENCY_ERROR",
  FREQUENCY_SET = "FREQUENCY_SET",
  AMOUNT_ERROR = "AMOUNT_ERROR",
  PAYMENT_ADDED = "PAYMENT_ADDED",
  DONATE_COMPLETED = "DONATE_COMPLETED",
  CONFIGURE_BUTTON_STARTED = "CONFIGURE_BUTTON_STARTED",
  DONATE_RECEIVED = "DONATE_RECEIVED",
  CONFIGURE_BUTTON_CREATED = "CONFIGURE_BUTTON_CREATED",
  CONFIGURE_BUTTON_COPIED = "CONFIGURE_BUTTON_COPIED",
  ADMIN_ACCEPTED = "ADMIN_ACCEPTED",
  // Donate button loaded in a website associated to admins and just to the nonprofit
  BUTTON_LOADED_ADMIN = "BUTTON_LOADED_ADMIN",
  BUTTON_LOADED_NONPROFIT = "BUTTON_LOADED_NONPROFIT",
  STRIPE_ACCEPTED = "STRIPE_ACCEPTED",
  TIP_UP = "TIP_UP",
  TIP_DOWN = "TIP_DOWN",
  TIP_ZERO = "TIP_ZERO",
  // Triggered when nonprofit admin welcome module is opened
  ADMIN_WELCOME_MODULE_OPEN = "ADMIN_WELCOME_MODULE_OPEN",
  // Triggered when nonprofit admin welcome module is closed
  ADMIN_WELCOME_MODULE_CLOSE = "ADMIN_WELCOME_MODULE_CLOSE",
  // Triggered when nonprofit admin guide module is opened
  ADMIN_NONPROFIT_GUIDE_OPEN = "ADMIN_NONPROFIT_GUIDE_OPEN",
  // Triggered when nonprofit admin guide module is closed
  ADMIN_NONPROFIT_GUIDE_CLOSE = "ADMIN_NONPROFIT_GUIDE_CLOSE",
  // Triggered when a nonprofit admin clicks an item in the guide module
  ADMIN_NONPROFIT_GUIDE_ITEM_CLICK = "ADMIN_NONPROFIT_GUIDE_ITEM_CLICK",
  // Triggered when a nonprofit admin closes the guide without clicking any item
  ADMIN_NONPROFIT_GUIDE_NO_ITEM_CLICK = "ADMIN_NONPROFIT_GUIDE_NO_ITEM_CLICK",
  // Triggered when an update post is published
  UPDATE_CREATED = "UPDATE_CREATED",
  // Triggered when a donor clicks through to donate from an update post
  UPDATE_DONATE_CLICKED = "UPDATE_DONATE_CLICKED",
  // Triggered when a published update post detail page is viewed
  UPDATE_VIEWED = "UPDATE_VIEWED",
  // Triggered when a fundraiser is created, once for each nonprofit it benefits
  FUNDRAISER_CREATED = "FUNDRAISER_CREATED",
  // Triggered when an edit to a nonprofit's profile is applied
  NONPROFIT_PROFILE_SAVED = "NONPROFIT_PROFILE_SAVED",
}
